"use client"

import { useEffect, useState } from "react"
import { observer } from "mobx-react-lite"
import { useAppContext } from "../../../../shared/functions/Context"
import {
  Users,
  FileText,
  Download,
  Eye,
  Building2,
  Briefcase,
  Star,
  Activity,
  X,
  User,
  Crown,
  Heart,
} from "lucide-react"
import { GenerateUserPDFReport } from "./components/GenerateUserPDFReport"
import GenerateAllPDFReports from "./components/GenerateAllPDFReports"

// Define AveragedRating type if not already defined
type AveragedRating = {
  rateeId: string
  valueAverages: { [key: string]: number }
  leadershipAverages: { [key: string]: number }
  // max: include professional/template averages
  templateAverages: { [key: string]: number }
  overallValueAverage: number
  overallLeadershipAverage: number
  overallTemplateAverage: number
  finalOverallAverage: number
}

export const UserRatingReport = observer(() => {
  const { api, store } = useAppContext()
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [averagedRatings, setAveragedRatings] = useState<AveragedRating[]>([])
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null)
  const [currentPage, setCurrentPage] = useState(1)
  const [itemsPerPage] = useState(10)

  useEffect(() => {
    const fetchData = async () => {
      try {
        await Promise.all([api.user.getAll(), api.department.getAll(), api.ratingAssignments.getAll()])
        const users = store.user.all.map((user) => user.asJson)
        const inProgressAssignment = store.ratingAssignments.all.find(
          (rate) => rate.asJson.status === "In Progress",
        )?.asJson

        if (inProgressAssignment && users.length && inProgressAssignment.description) {
          const uids = users.map((user) => user.uid)
          await Promise.all([
            api.valueRating.getAllNew(uids, inProgressAssignment.description),
            api.leadershipRating.getAllNew(uids, inProgressAssignment.description),
          ])
        }
        calculateAverages()
      } catch (error) {
        console.error("Error fetching data:", error)
        setError("An error occurred while fetching data. Please try again later.")
      } finally {
        setLoading(false)
      }
    }

    fetchData()
  }, [api, store])

  const calculateAverages = () => {
    const valueRatings = store.valueRating.all.map((rating) => rating.asJson)
    const leadershipRatings = store.leadershipRating.all.map((rating) => rating.asJson)
    const templateRatings = store.templateRating.all.map((rating) => rating.asJson)

    const averagedRatings: AveragedRating[] = store.user.all.map((user) => {
      const rateeId = user.asJson.uid
      const userValueRatings = valueRatings.filter((rating) => rating.rateeId === rateeId)
      const userLeadershipRatings = leadershipRatings.filter((rating) => rating.rateeId === rateeId)
      const userTemplateRatings = templateRatings.filter((rating) => rating.rateeId === rateeId)

      const valueAverages: { [key: string]: number } = {}
      const leadershipAverages: { [key: string]: number } = {}
      const templateTotals: { [key: string]: number } = {}
      const templateCounts: { [key: string]: number } = {}

      // Calculate value averages
      userValueRatings.forEach((rating) => {
        Object.entries(rating.values).forEach(([, raterData]) => {
          Object.entries(raterData.ratings).forEach(([valueName, statements]) => {
            if (!valueAverages[valueName]) valueAverages[valueName] = 0
            let total = 0
            let count = 0
            Object.values(statements).forEach((rating) => {
              if (Array.isArray(rating)) {
                total += rating.reduce((sum, val) => sum + val, 0)
                count += rating.length
              } else {
                total += rating
                count++
              }
            })
            valueAverages[valueName] += total / count
          })
        })
      })

      // Calculate leadership averages
      userLeadershipRatings.forEach((rating) => {
        Object.entries(rating.criteria).forEach(([, raterData]) => {
          Object.entries(raterData.ratings).forEach(([criteriaName, rating]) => {
            if (!leadershipAverages[criteriaName]) leadershipAverages[criteriaName] = 0
            if (typeof rating === "number") {
              leadershipAverages[criteriaName] += rating
            } else if (typeof rating === "object") {
              const avgRating = Object.values(rating).reduce((sum, val) => sum + val, 0) / Object.values(rating).length
              leadershipAverages[criteriaName] += avgRating
            }
          })
        })
      })

      // First pass: accumulate totals and counts per criteria (across all raters and statements)
      userTemplateRatings.forEach((rating) => {
        Object.entries(rating.values || {}).forEach(([, raterData]) => {
          Object.entries(raterData.ratings || {}).forEach(([criteriaName, statements]) => {
            if (!templateTotals[criteriaName]) {
              templateTotals[criteriaName] = 0
              templateCounts[criteriaName] = 0
            }
            Object.values(statements).forEach((val) => {
              if (Array.isArray(val)) {
                templateTotals[criteriaName] += val.reduce((s: number, v: number) => s + v, 0)
                templateCounts[criteriaName] += val.length
              } else if (typeof val === "number") {
                templateTotals[criteriaName] += val
                templateCounts[criteriaName]++
              }
            })
          })
        })
      })

      // Second pass: calculate final averages per criteria
      const templateAverages: { [key: string]: number } = {}
      Object.keys(templateTotals).forEach((key) => {
        templateAverages[key] = templateTotals[key] / templateCounts[key] || 0
      })

      // Finalize averages
      Object.keys(valueAverages).forEach((key) => {
        valueAverages[key] /= userValueRatings.length || 1
      })
      Object.keys(leadershipAverages).forEach((key) => {
        leadershipAverages[key] /= userLeadershipRatings.length || 1
      })

      const overallValueAverage =
        Object.values(valueAverages).reduce((sum, val) => sum + val, 0) / (Object.keys(valueAverages).length || 1)
      const overallLeadershipAverage =
        Object.values(leadershipAverages).reduce((sum, val) => sum + val, 0) /
        (Object.keys(leadershipAverages).length || 1)
      const overallTemplateAverage =
        Object.values(templateAverages).length > 0
          ? Object.values(templateAverages).reduce((sum, val) => sum + val, 0) / Object.keys(templateAverages).length
          : 0

      const finalOverallAverage = (() => {
        const scores = [overallValueAverage, overallLeadershipAverage, overallTemplateAverage].filter(
          (score) => score > 0,
        )
        return scores.length > 0 ? scores.reduce((sum, val) => sum + val, 0) / scores.length : 0
      })()

      return {
        rateeId,
        valueAverages,
        leadershipAverages,
        // ensure templateAverages values are numbers
        templateAverages: Object.fromEntries(
          Object.entries(templateAverages).map(([k, v]) => [k, Number(isFinite(v) ? v : 0)]),
        ),
        overallValueAverage,
        overallLeadershipAverage,
        overallTemplateAverage: Number(isFinite(overallTemplateAverage) ? overallTemplateAverage : 0),
        finalOverallAverage,
      }
    })

    setAveragedRatings(averagedRatings.sort((a, b) => b.finalOverallAverage - a.finalOverallAverage))
  }

  const handleCloseModal = () => {
    setSelectedUserId(null)
  }

  const handleGeneratePDF = (userId: string) => {
    const selectedUserData = averagedRatings.find((rating) => rating.rateeId === userId)
    if (selectedUserData) {
      const userDetails = store.user.all.find((user) => user.asJson.uid === userId)?.asJson
      const userData = {
        displayName: getDisplayNameById(userId),
        departmentName: getDepartmentName(userDetails?.department),
        jobTitle: userDetails?.jobTitle || "",
        finalOverallAverage: selectedUserData.finalOverallAverage.toFixed(2),
        overallValueAverage: selectedUserData.overallValueAverage.toFixed(2),
        overallLeadershipAverage: selectedUserData.overallLeadershipAverage.toFixed(2),
        overallTemplateAverage: selectedUserData.overallTemplateAverage.toFixed(2),
        valueAverages: selectedUserData.valueAverages,
        leadershipAverages: selectedUserData.leadershipAverages,
        // max: include professional/template averages in individual PDF
        templateAverages: selectedUserData.templateAverages,
      }
      GenerateUserPDFReport(userData)
    }
  }

  const getDisplayNameById = (userId: string) => {
    const user = store.user.all.find((user) => user.asJson.uid === userId)?.asJson
    return user ? user.displayName : "Unknown User"
  }

  const getDepartmentName = (departmentId: string | undefined) => {
    if (!departmentId) return "N/A"
    const department = store.department.all.find((department) => department.asJson.id === departmentId)?.asJson
    return department ? (department.name ?? "") : "N/A"
  }

  // Pagination logic
  const totalPages = Math.ceil(averagedRatings.length / itemsPerPage)
  const startIndex = (currentPage - 1) * itemsPerPage
  const endIndex = startIndex + itemsPerPage
  const currentItems = averagedRatings.slice(startIndex, endIndex)

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64 w-full">
        <div className="relative">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-200"></div>
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-600 border-t-transparent absolute top-0 left-0"></div>
          <div className="absolute inset-0 flex items-center justify-center">
            <Activity className="h-5 w-5 text-blue-600 animate-pulse" />
          </div>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-64 w-full">
        <div className="text-center space-y-4">
          <div className="p-4 bg-red-100 rounded-full w-fit mx-auto">
            <FileText className="h-8 w-8 text-red-600" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-slate-900 mb-2">Error Loading Reports</h3>
            <p className="text-slate-600">{error}</p>
          </div>
        </div>
      </div>
    )
  }

  const selectedUserData = averagedRatings.find((rating) => rating.rateeId === selectedUserId)

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-blue-100 rounded-lg">
            <Users className="h-5 w-5 text-blue-600" />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-slate-800">User Rating Reports</h2>
            <p className="text-slate-600">Comprehensive performance analysis for all employees</p>
          </div>
        </div>
        <GenerateAllPDFReports ratings={averagedRatings} />
      </div>

      {/* Main Table */}
      <div className="bg-white rounded-2xl shadow-lg border border-slate-200/50 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gradient-to-r from-slate-50 to-blue-50 border-b border-slate-200">
              <tr>
                <th className="text-left py-4 px-6 font-semibold text-slate-700">Employee</th>
                <th className="text-center py-4 px-6 font-semibold text-slate-700">Department</th>
                <th className="text-center py-4 px-6 font-semibold text-slate-700">Job Title</th>
                <th className="text-center py-4 px-6 font-semibold text-slate-700">Values</th>
                <th className="text-center py-4 px-6 font-semibold text-slate-700">Leadership</th>
                <th className="text-center py-4 px-6 font-semibold text-slate-700">Professional</th>
                <th className="text-center py-4 px-6 font-semibold text-slate-700">Overall</th>
                <th className="text-center py-4 px-6 font-semibold text-slate-700">Actions</th>
              </tr>
            </thead>
            <tbody>
              {currentItems.map((rating, index) => {
                const rateeName = getDisplayNameById(rating.rateeId)
                const userDetails = store.user.all.find((user) => user.asJson.uid === rating.rateeId)?.asJson
                const departmentName = userDetails ? getDepartmentName(userDetails.department) : ""
                const jobTitle = userDetails?.jobTitle || ""

                return (
                  <tr key={rating.rateeId} className="border-b border-slate-100 hover:bg-slate-50 transition-colors">
                    <td className="py-4 px-6">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-600 rounded-lg flex items-center justify-center">
                          <User className="h-5 w-5 text-white" />
                        </div>
                        <div>
                          <div className="font-semibold text-slate-800">{rateeName}</div>
                          <div className="text-sm text-slate-600">ID: {rating.rateeId.slice(0, 8)}...</div>
                        </div>
                      </div>
                    </td>
                    <td className="py-4 px-6 text-center">
                      <div className="flex items-center justify-center gap-2">
                        <Building2 className="h-4 w-4 text-blue-600" />
                        <span className="text-sm font-medium text-slate-700">{departmentName}</span>
                      </div>
                    </td>
                    <td className="py-4 px-6 text-center">
                      <div className="flex items-center justify-center gap-2">
                        <Briefcase className="h-4 w-4 text-purple-600" />
                        <span className="text-sm font-medium text-slate-700">{jobTitle}</span>
                      </div>
                    </td>
                    <td className="py-4 px-6 text-center">
                      <div className="flex items-center justify-center gap-2">
                        <span className="text-lg font-bold text-slate-800">
                          {rating.overallValueAverage.toFixed(2)}
                        </span>
                        <div className="w-12 bg-slate-200 rounded-full h-2">
                          <div
                            className="h-full bg-blue-500 rounded-full transition-all duration-500"
                            style={{ width: `${(rating.overallValueAverage / 5) * 100}%` }}
                          ></div>
                        </div>
                      </div>
                    </td>
                    <td className="py-4 px-6 text-center">
                      <div className="flex items-center justify-center gap-2">
                        <span className="text-lg font-bold text-slate-800">
                          {rating.overallLeadershipAverage.toFixed(2)}
                        </span>
                        <div className="w-12 bg-slate-200 rounded-full h-2">
                          <div
                            className="h-full bg-purple-500 rounded-full transition-all duration-500"
                            style={{ width: `${(rating.overallLeadershipAverage / 5) * 100}%` }}
                          ></div>
                        </div>
                      </div>
                    </td>
                    <td className="py-4 px-6 text-center">
                      <div className="flex items-center justify-center gap-2">
                        <span className="text-lg font-bold text-slate-800">
                          {rating.overallTemplateAverage?.toFixed
                            ? rating.overallTemplateAverage.toFixed(2)
                            : (rating.overallTemplateAverage || 0).toFixed(2)}
                        </span>
                        <div className="w-12 bg-slate-200 rounded-full h-2">
                          <div
                            className="h-full bg-emerald-500 rounded-full transition-all duration-500"
                            style={{ width: `${(rating.overallTemplateAverage / 5) * 100}%` }}
                          ></div>
                        </div>
                      </div>
                    </td>
                    <td className="py-4 px-6 text-center">
                      <div className="flex items-center justify-center gap-2">
                        <Star className="h-4 w-4 text-amber-500" />
                        <span className="text-xl font-bold text-slate-800">
                          {rating.finalOverallAverage.toFixed(2)}
                        </span>
                      </div>
                    </td>
                    <td className="py-4 px-6">
                      <div className="flex items-center justify-center gap-2">
                        <button
                          onClick={() => setSelectedUserId(rating.rateeId)}
                          className="p-2 bg-blue-100 hover:bg-blue-200 rounded-lg transition-colors"
                          title="View Details"
                        >
                          <Eye className="h-4 w-4 text-blue-600" />
                        </button>
                        <button
                          onClick={() => handleGeneratePDF(rating.rateeId)}
                          className="p-2 bg-green-100 hover:bg-green-200 rounded-lg transition-colors"
                          title="Generate PDF"
                        >
                          <Download className="h-4 w-4 text-green-600" />
                        </button>
                      </div>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="bg-slate-50 px-6 py-4 border-t border-slate-200">
          <div className="flex items-center justify-between">
            <div className="text-sm text-slate-600">
              Showing {startIndex + 1} to {Math.min(endIndex, averagedRatings.length)} of {averagedRatings.length}{" "}
              results
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
                className="px-3 py-2 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                Previous
              </button>
              <span className="px-3 py-2 text-sm font-medium text-slate-700">
                Page {currentPage} of {totalPages}
              </span>
              <button
                onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                disabled={currentPage === totalPages}
                className="px-3 py-2 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                Next
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Modal */}
      {selectedUserId && selectedUserData && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-blue-50 to-purple-50 p-6 border-b border-slate-200">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-purple-600 rounded-xl flex items-center justify-center">
                    <User className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-slate-800">
                      {getDisplayNameById(selectedUserData.rateeId)}
                    </h3>
                    <p className="text-slate-600">Detailed Performance Report</p>
                  </div>
                </div>
                <button onClick={handleCloseModal} className="p-2 hover:bg-slate-200 rounded-lg transition-colors">
                  <X className="h-5 w-5 text-slate-600" />
                </button>
              </div>
            </div>

            {/* Modal Content */}
            <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
              <div className="space-y-6">
                {/* Employee Info */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="bg-blue-50 rounded-xl p-4">
                    <div className="flex items-center gap-3 mb-2">
                      <Building2 className="h-5 w-5 text-blue-600" />
                      <span className="font-semibold text-slate-800">Department</span>
                    </div>
                    <p className="text-slate-700">
                      {getDepartmentName(
                        store.user.all.find((user) => user.asJson.uid === selectedUserData.rateeId)?.asJson.department,
                      )}
                    </p>
                  </div>
                  <div className="bg-purple-50 rounded-xl p-4">
                    <div className="flex items-center gap-3 mb-2">
                      <Briefcase className="h-5 w-5 text-purple-600" />
                      <span className="font-semibold text-slate-800">Job Title</span>
                    </div>
                    <p className="text-slate-700">
                      {store.user.all.find((user) => user.asJson.uid === selectedUserData.rateeId)?.asJson.jobTitle ||
                        "N/A"}
                    </p>
                  </div>
                  <div className="bg-amber-50 rounded-xl p-4">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="p-3 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl shadow-lg">
                        <Star className="h-6 w-6 text-white" />
                      </div>
                      <div>
                        <h3 className="text-xl font-bold text-slate-800">Overall Rating</h3>
                        <p className="text-slate-600">Combined rating across all categories</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-3xl font-bold text-slate-800">
                        {selectedUserData.finalOverallAverage.toFixed(2)}
                      </div>
                      <div className="text-sm text-slate-600">out of 5.00</div>
                    </div>
                  </div>
                </div>

                {/* Overall Performance Summary */}
                <div className="bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 rounded-2xl p-6 border border-blue-100/50">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className="p-3 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl shadow-lg">
                        <Star className="h-6 w-6 text-white" />
                      </div>
                      <div>
                        <h3 className="text-xl font-bold text-slate-800">Overall Performance</h3>
                        <p className="text-slate-600">Combined rating across all categories</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-3xl font-bold text-slate-800">
                        {selectedUserData.finalOverallAverage.toFixed(2)}
                      </div>
                      <div className="text-sm text-slate-600">out of 5.00</div>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="bg-white/60 backdrop-blur-sm rounded-xl p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <Heart className="h-4 w-4 text-blue-600" />
                        <span className="text-sm font-semibold text-slate-700">Values</span>
                      </div>
                      <div className="text-2xl font-bold text-slate-800">
                        {selectedUserData.overallValueAverage.toFixed(2)}
                      </div>
                    </div>
                    <div className="bg-white/60 backdrop-blur-sm rounded-xl p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <Crown className="h-4 w-4 text-purple-600" />
                        <span className="text-sm font-semibold text-slate-700">Leadership</span>
                      </div>
                      <div className="text-2xl font-bold text-slate-800">
                        {selectedUserData.overallLeadershipAverage.toFixed(2)}
                      </div>
                    </div>
                    <div className="bg-white/60 backdrop-blur-sm rounded-xl p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <Briefcase className="h-4 w-4 text-emerald-600" />
                        <span className="text-sm font-semibold text-slate-700">Professional</span>
                      </div>
                      <div className="text-2xl font-bold text-slate-800">
                        {selectedUserData.overallTemplateAverage?.toFixed
                          ? selectedUserData.overallTemplateAverage.toFixed(2)
                          : (selectedUserData.overallTemplateAverage || 0).toFixed(2)}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex gap-4">
                  <button
                    onClick={() => handleGeneratePDF(selectedUserId)}
                    className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-xl hover:from-green-700 hover:to-emerald-700 transition-all duration-300 shadow-lg"
                  >
                    <Download className="h-4 w-4" />
                    <span className="font-semibold">Generate PDF Report</span>
                  </button>
                </div>

                {/* Detailed Breakdowns */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                  {/* Value Ratings Breakdown */}
                  <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                    <div className="bg-blue-50 p-4 border-b border-slate-200">
                      <div className="flex items-center gap-3">
                        <Heart className="h-5 w-5 text-blue-600" />
                        <h4 className="text-lg font-semibold text-slate-800">Value Ratings Breakdown</h4>
                      </div>
                    </div>
                    <div className="p-4">
                      <div className="space-y-3">
                        {Object.entries(selectedUserData.valueAverages).map(([name, avg]) => (
                          <div key={name} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                            <span className="font-medium text-slate-700">{name}</span>
                            <div className="flex items-center gap-2">
                              <span className="font-bold text-slate-800">{avg.toFixed(2)}</span>
                              <div className="w-16 bg-slate-200 rounded-full h-2">
                                <div
                                  className="h-full bg-blue-500 rounded-full transition-all duration-500"
                                  style={{ width: `${(avg / 5) * 100}%` }}
                                ></div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Leadership Ratings Breakdown */}
                  <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                    <div className="bg-purple-50 p-4 border-b border-slate-200">
                      <div className="flex items-center gap-3">
                        <Crown className="h-5 w-5 text-purple-600" />
                        <h4 className="text-lg font-semibold text-slate-800">Leadership Ratings Breakdown</h4>
                      </div>
                    </div>
                    <div className="p-4">
                      <div className="space-y-3">
                        {Object.entries(selectedUserData.leadershipAverages).map(([name, avg]) => (
                          <div key={name} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                            <span className="font-medium text-slate-700">{name}</span>
                            <div className="flex items-center gap-2">
                              <span className="font-bold text-slate-800">{avg.toFixed(2)}</span>
                              <div className="w-16 bg-slate-200 rounded-full h-2">
                                <div
                                  className="h-full bg-purple-500 rounded-full transition-all duration-500"
                                  style={{ width: `${(avg / 5) * 100}%` }}
                                ></div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Professional Competency Ratings Breakdown */}
                  <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                    <div className="bg-emerald-50 p-4 border-b border-slate-200">
                      <div className="flex items-center gap-3">
                        <Briefcase className="h-5 w-5 text-emerald-600" />
                        <h4 className="text-lg font-semibold text-slate-800">Professional Ratings Breakdown</h4>
                      </div>
                    </div>
                    <div className="p-4">
                      <div className="space-y-3">
                        {Object.entries(selectedUserData.templateAverages || {}).length > 0 ? (
                          Object.entries(selectedUserData.templateAverages).map(([name, avg]) => (
                            <div key={name} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                              <span className="font-medium text-slate-700">{name}</span>
                              <div className="flex items-center gap-2">
                                <span className="font-bold text-slate-800">{avg.toFixed(2)}</span>
                                <div className="w-16 bg-slate-200 rounded-full h-2">
                                  <div
                                    className="h-full bg-emerald-500 rounded-full transition-all duration-500"
                                    style={{ width: `${(avg / 5) * 100}%` }}
                                  ></div>
                                </div>
                              </div>
                            </div>
                          ))
                        ) : (
                          <div className="text-center py-4 text-slate-500">
                            No professional competency ratings available
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
})
